
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserProfile } from "@/components/UserProfile";
import { ActivityLog } from "@/components/ActivityLog";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const UserProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  
  const isCurrentUser = user?.id === id;
  const canViewOtherProfiles = hasPermission("view:users");
  
  useEffect(() => {
    // Redirect if not authorized to view this profile
    if (!id || (!isCurrentUser && !canViewOtherProfiles)) {
      navigate("/dashboard");
    }
  }, [id, isCurrentUser, canViewOtherProfiles, navigate]);

  const handleBack = () => {
    if (canViewOtherProfiles && !isCurrentUser) {
      navigate("/dashboard/users");
    } else {
      navigate("/dashboard");
    }
  };

  if (!id) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 max-w-6xl mx-auto"
    >
      <div className="mb-6 flex items-center">
        <Button 
          variant="ghost" 
          size="sm" 
          className="mr-4"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <User className="h-5 w-5" />
          {isCurrentUser ? "My Profile" : "User Profile"}
        </h1>
      </div>

      <div className="space-y-8">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile">
            <Card>
              <CardContent className="pt-6">
                <UserProfile userId={id} readOnly={!isCurrentUser} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="activity">
            <Card>
              <CardContent className="pt-6">
                <ActivityLog userId={id} limit={20} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
};

export default UserProfilePage;
